import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import AppShell from "@/components/layout/AppShell";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Sparkles, Check } from "lucide-react";

// Brand settings — one logo per workspace, auto-applied to every image
// (and video, once Phase D lands).

const TOKEN_KEY = "nx_token";
const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_MB = 5;

function apiFetch(path, opts = {}) {
  return fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
}

export default function BrandPage() {
  const { lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ has_logo: false });
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const loadLogo = async () => {
    try {
      const r = await apiFetch("/api/brand/logo");
      const d = await r.json();
      setMeta(d);
      if (d.has_logo) {
        const fr = await apiFetch("/api/brand/logo/file");
        if (fr.ok) {
          const b = await fr.blob();
          setPreview(URL.createObjectURL(b));
        }
      } else {
        setPreview(null);
      }
    } catch {
      toast.error(lang === "ar" ? "فشل تحميل الشعار" : "Failed to load logo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadLogo(); /* eslint-disable-next-line */ }, []);

  const handleUpload = async (file) => {
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error(lang === "ar" ? "نوع الملف غير مدعوم" : "Unsupported file type");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(lang === "ar" ? `الملف كبير جداً (${MAX_MB} ميغابايت كحد أقصى)` : `File too large (max ${MAX_MB} MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/api/brand/logo", { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      toast.success(lang === "ar" ? "تم تحديث الشعار — سيظهر تلقائياً على كل صورة" : "Logo saved — auto-applied to every future image");
      await loadLogo();
    } catch {
      toast.error(lang === "ar" ? "فشل الرفع" : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(lang === "ar" ? "إزالة الشعار؟" : "Remove the logo?")) return;
    try {
      const r = await apiFetch("/api/brand/logo", { method: "DELETE" });
      if (!r.ok) throw new Error();
      toast.success(lang === "ar" ? "تمت الإزالة" : "Removed");
      setMeta({ has_logo: false });
      setPreview(null);
    } catch {
      toast.error(lang === "ar" ? "فشل الحذف" : "Delete failed");
    }
  };

  return (
    <AppShell>
      <div data-testid="brand-page" className="space-y-6 max-w-3xl">
        <header>
          <h1 className="text-2xl md:text-3xl font-display font-semibold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            {lang === "ar" ? "الهوية والعلامة التجارية" : "Brand & Watermark"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === "ar"
              ? "ارفع شعار علامتك التجارية مرة واحدة. سنضعه تلقائياً على كل صورة وفيديو تنشئه DAAM."
              : "Upload your brand logo once. DAAM will automatically place it on every image and video you generate."}
          </p>
        </header>

        <section className="bg-card border border-border/60 rounded-md p-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-2">
                {lang === "ar" ? "المعاينة الحالية" : "Current logo"}
              </label>
              <div
                data-testid="brand-logo-preview"
                className="aspect-square w-full max-w-xs rounded-md border border-dashed border-border/60 bg-muted/30 flex items-center justify-center overflow-hidden"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                ) : preview ? (
                  <img src={preview} alt="logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-xs text-muted-foreground text-center px-4">
                    {lang === "ar" ? "لم يتم رفع أي شعار بعد" : "No logo uploaded yet"}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">
                  {lang === "ar" ? "رفع شعار جديد" : "Upload new logo"}
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  data-testid="brand-upload-input"
                  onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                />
                <button
                  data-testid="brand-upload-btn"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 text-xs px-4 py-2.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {lang === "ar" ? "اختيار ملف" : "Choose file"}
                </button>
                <p className="text-[11px] text-muted-foreground mt-2">
                  {lang === "ar"
                    ? "PNG بخلفية شفافة يعطي أفضل نتيجة. الحد الأقصى 5 ميغابايت."
                    : "PNG with transparent background works best. Max 5 MB."}
                </p>
              </div>
              {meta.has_logo && (
                <button
                  data-testid="brand-delete-btn"
                  onClick={handleDelete}
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-sm border border-border/60 hover:border-red-500 hover:text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {lang === "ar" ? "إزالة الشعار" : "Remove logo"}
                </button>
              )}
              <div className="p-3 rounded-sm bg-primary/5 border border-primary/20 text-xs leading-relaxed">
                <div className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                  <span>
                    {lang === "ar"
                      ? "بمجرد الرفع، سيُضاف شعارك تلقائياً كعلامة مائية على كل الصور والفيديوهات الجديدة."
                      : "Once uploaded, your logo is auto-added as a watermark on every new image and video."}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
